import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { InvoiceModel } from '@data-layer/models/entry-summaries';
import { IncoTermModel } from '@data-layer/models/common';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { CompaniesService } from '@services/companies.service';
import { UserBetaFlagModel } from '@app/data-layer/models/user';
import {
  MANUFACTURER_EXPECTED_TO_BE_CANADIAN,
  MISSING_ADDRESS,
  MISSING_ADDRESS_PARTS,
  MISSING_CANADA_STATE,
  MISSING_CITY,
  MISSING_COUNTRY,
  MISSING_POSTAL_CODE,
  MISSING_STATE,
} from '@constants/entity-validation-messages';

@Component({
  selector: 'es-details-invoice-ocr-card-main-desktop',
  templateUrl: './es-details-invoice-ocr-card-main-desktop.component.html',
  styleUrls: ['./es-details-invoice-ocr-card-main-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsDetailsInvoiceOcrCardMainDesktopComponent {
  @Input() public invoice: InvoiceModel;
  @Input() public previewImgUrl: string;
  @Input() public totalEnteredValueInUSD: number;
  @Input() public totalEnteredValue: number;
  @Input() public totalGrossWeight: number;
  @Input() public isShowCollapse: boolean;
  @Input() public incoterms: IncoTermModel[] = [];
  @Input() public isOpenedItems: boolean;
  @Input() public isEditable: boolean;
  // Additive granular edit affordances (broker page). Each falls back to `isEditable` when
  // undefined, so user-side / admin usages are unchanged. Let the broker enable only the
  // invoice-details edit + delete while the rest of the card stays read-only.
  @Input() public canEditInvoiceDetails: boolean;
  @Input() public canDeleteInvoice: boolean;
  @Input() public canEditLineItems: boolean;
  @Input() public canClearInvoice: boolean;
  @Input() public canEditManufacturer: boolean;

  @Output() public editInvoice: EventEmitter<void> = new EventEmitter<void>();
  @Output() public editMF: EventEmitter<void> = new EventEmitter<void>();
  @Output() public addLineItem: EventEmitter<void> = new EventEmitter<void>();
  @Output() public deleteInvoice: EventEmitter<void> = new EventEmitter<void>();
  @Output() public clearData: EventEmitter<void> = new EventEmitter<void>();
  @Output() public openItems: EventEmitter<number> = new EventEmitter();
  @Output() public closeItems: EventEmitter<number> = new EventEmitter();
  @Output() public inputElementClick: EventEmitter<number> = new EventEmitter();

  constructor(private companiesService: CompaniesService) {}

  public get getIncotermName(): string {
    const code: string = this.invoice.incoTermsCode;
    const incoterm: IncoTermModel = (this.incoterms || []).find((item: IncoTermModel) => item.code === code);
    return incoterm ? `${incoterm.code} ${incoterm.name}` : code;
  }

  public get isValidEntityAddress(): boolean {
    const manufacturer = this.invoice?.manufacturer;
    const missingAddressKeys: string[] = ['country', 'city', 'line1'];
    const isFilledAddress: boolean = missingAddressKeys.every((item: string) => manufacturer?.address?.[item]);

    // #13022: backend emits address-related errors (e.g. MISSING_STATE for US/CA/MX) on the
    // entity-level validationMessages, not address-level. Mirror desktop-summary-entity.getIsValid.
    const addressLevelErrorTexts: readonly string[] = [
      MISSING_ADDRESS,
      MISSING_ADDRESS_PARTS,
      MISSING_CITY,
      MISSING_COUNTRY,
      MISSING_STATE,
      MISSING_CANADA_STATE,
      MISSING_POSTAL_CODE,
    ];
    const hasEntityAddressErrors = (manufacturer?.validationMessages ?? []).some((m) => addressLevelErrorTexts.includes(m.text));

    return EntityValidationHelper.isValidEntityAddress(manufacturer?.address) && isFilledAddress && !hasEntityAddressErrors;
  }

  public get isAnyInvoiceMainFieldsInvalid(): boolean {
    return !this.invoice.invoiceNumber || !this.invoice.currency || !this.invoice?.countryOfExport?.description;
  }

  public get isAnyManufacturerFieldsInvalid(): boolean {
    return !this.invoice?.manufacturer?.name || !this.invoice?.manufacturer?.address || !this.isValidEntityAddress;
  }

  public get isAllManufacturerFieldsInvalid(): boolean {
    return (
      this.isHasEsOcrRedesign && !this.invoice?.manufacturer?.name && (!this.invoice?.manufacturer?.address || !this.isValidEntityAddress)
    );
  }

  public get isManufacturerExpectedFromCanada(): boolean {
    return (
      !!this.invoice?.validationMessages?.some((m) => m.text === MANUFACTURER_EXPECTED_TO_BE_CANADIAN) ||
      !!this.invoice?.manufacturer?.validationMessages?.some((m) => m.text === MANUFACTURER_EXPECTED_TO_BE_CANADIAN)
    );
  }

  public get isHasEsOcrRedesign(): boolean {
    return this.companiesService
      .getUser()
      ?.betaFlags?.map((flag: UserBetaFlagModel) => flag.tag)
      .includes(enumUserBetaFlag.esOcrRedesign);
  }
}
